/** One public read per resource, shared by every mounted screen in this tab. */
import { useEffect, useState } from 'react';

export const mayPoll = () => (typeof document === 'undefined' || document.visibilityState !== 'hidden') &&
  (typeof navigator === 'undefined' || navigator.onLine !== false);

export function retryAfterMs(value: string | null, now = Date.now()): number {
  if (!value) return 0;
  const seconds = Number(value);
  if (Number.isFinite(seconds) && seconds >= 0) return seconds * 1000;
  const date = Date.parse(value);
  return Number.isFinite(date) ? Math.max(0, date - now) : 0;
}

export class ReadUnavailable extends Error {
  constructor(public status: number, public retryMs = 0) { super(`read-unavailable:${status}`); }
}

interface ReadSpec<T> {
  key: string;
  initial: () => T;
  load: () => Promise<T>;
  interval: (value: T) => number;
  onError?: (value: T, error: unknown) => T;
}
interface SharedRead<T> {
  value: T;
  spec: ReadSpec<T>;
  listeners: Set<(value: T) => void>;
  timer: ReturnType<typeof setTimeout> | null;
  pending: Promise<void> | null;
  checkedAt: number;
  retryAt: number;
  failures: number;
}
const reads = new Map<string, SharedRead<unknown>>();
let eventsInstalled = false;

function schedule<T>(entry: SharedRead<T>): void {
  if (entry.timer) clearTimeout(entry.timer);
  entry.timer = null;
  if (!entry.listeners.size || !mayPoll()) return;
  const interval = Math.max(30_000, entry.spec.interval(entry.value));
  const due = Math.max(entry.checkedAt + interval, entry.retryAt);
  entry.timer = setTimeout(() => void tick(entry), Math.max(1, due - Date.now()));
}
async function tick<T>(entry: SharedRead<T>): Promise<void> {
  if (!mayPoll()) { schedule(entry); return; }
  if (entry.pending) return entry.pending;
  const interval = Math.max(30_000, entry.spec.interval(entry.value));
  if (Date.now() < Math.max(entry.checkedAt + interval, entry.retryAt)) { schedule(entry); return; }
  entry.pending = (async () => {
    try {
      const value = await entry.spec.load();
      entry.value = value;
      entry.failures = 0;
      entry.retryAt = 0;
      entry.listeners.forEach((listener) => listener(value));
    } catch (error) {
      if (entry.spec.onError) {
        entry.value = entry.spec.onError(entry.value, error);
        entry.listeners.forEach((listener) => listener(entry.value));
      }
      entry.failures += 1;
      const backoff = Math.min(60 * 60_000, Math.max(interval, 60_000) * 2 ** Math.min(entry.failures - 1, 6));
      entry.retryAt = Date.now() + Math.max(backoff, error instanceof ReadUnavailable ? error.retryMs : 0);
    } finally {
      entry.checkedAt = Date.now();
      entry.pending = null;
      schedule(entry);
    }
  })();
  return entry.pending;
}
function installEvents(): void {
  if (eventsInstalled || typeof window === 'undefined') return;
  eventsInstalled = true;
  const update = () => reads.forEach((entry) => {
    if (!entry.listeners.size) return;
    if (!mayPoll()) { if (entry.timer) clearTimeout(entry.timer); entry.timer = null; }
    else void tick(entry);
  });
  window.addEventListener('online', update);
  window.addEventListener('offline', update);
  document.addEventListener('visibilitychange', update);
}
export function usePublicRead<T>(spec: ReadSpec<T>): T {
  const existing = reads.get(spec.key) as SharedRead<T> | undefined;
  const [value, setValue] = useState<T>(() => existing?.value ?? spec.initial());
  useEffect(() => {
    installEvents();
    let entry = reads.get(spec.key) as SharedRead<T> | undefined;
    if (!entry) {
      entry = { spec, value: spec.initial(), listeners: new Set(), timer: null, pending: null, checkedAt: -Infinity, retryAt: 0, failures: 0 };
      reads.set(spec.key, entry as SharedRead<unknown>);
    }
    entry.spec = spec;
    entry.listeners.add(setValue);
    setValue(entry.value);
    void tick(entry);
    return () => {
      entry!.listeners.delete(setValue);
      if (!entry!.listeners.size && entry!.timer) { clearTimeout(entry!.timer); entry!.timer = null; }
    };
  }, [spec.key]);
  return value;
}
export function resetPublicReads(): void {
  reads.forEach((entry) => { if (entry.timer) clearTimeout(entry.timer); });
  reads.clear();
}
